import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Phone, Clock, DollarSign, Users, PhoneOff, Calendar, Filter, FileText, Globe, Zap, Shield, Link2, Sliders } from "lucide-react";
import { Link } from "react-router-dom";
import PainPointCard from "@/components/landing/PainPointCard";
import SolutionCard from "@/components/landing/SolutionCard";
import ROICalculator from "@/components/landing/ROICalculator";
import IndustryTabs from "@/components/landing/IndustryTabs";
import ComparisonTable from "@/components/landing/ComparisonTable";
import heroImg from "@/assets/hero-ai-assistant.jpg";

const Landing = () => {
  return (
    <div className="min-h-screen">
      {/* Minimal Header */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="text-xl font-bold text-primary">
            Voice Vision Media
          </Link>
          <Button asChild size="lg">
            <Link to="/contact">Schedule Demo</Link>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Your Team is Overwhelmed. Your Customers are Waiting.{" "}
                <span className="text-primary">There's a Better Way.</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                Meet Your AI Virtual Employee—Available 24/7, Never Takes a Break,
                Scales with Your Business
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" asChild className="text-lg">
                  <a 
                    href="https://calendar.app.google/HfFwVFvYGx42io1B9" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Schedule a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <Button size="lg" variant="outline" asChild className="text-lg">
                  <a href="#calculator">Calculate Your Savings</a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src={heroImg}
                alt="AI Virtual Employee Dashboard"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Sound Familiar? You're Not Alone.
            </h2>
            <p className="text-xl text-muted-foreground">
              These problems cost businesses thousands every month
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PainPointCard
              icon={PhoneOff}
              title="Missed Calls = Lost Revenue"
              stat="73% won't call back"
              description="Every missed call is a customer choosing your competitor"
            />
            <PainPointCard
              icon={DollarSign}
              title="Hiring is Expensive"
              stat="$4,700 per employee"
              description="Average cost to hire plus weeks of training time"
            />
            <PainPointCard
              icon={Clock}
              title="After-Hours Inquiries"
              stat="60% outside business hours"
              description="Most customer inquiries happen when you're closed"
            />
            <PainPointCard
              icon={Users}
              title="Repetitive Tasks Drain Your Team"
              stat="20+ hours per week"
              description="Your skilled team wastes time on scheduling and FAQs"
            />
          </div>
        </div>
      </section>

      {/* Solution Overview */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Your Virtual Employee in 3 Simple Steps
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our proven onboarding process ensures your AI employee is fully operational within 48-72 hours, with minimal disruption to your current operations
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
            <Card>
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Sliders className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">1. Customize & Configure</h3>
                <p className="text-muted-foreground mb-4">
                  We deeply analyze your business processes, customer interactions, and brand voice. Your Virtual Employee is trained on your specific services, pricing, policies, and communication style.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Business process mapping</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Brand voice training</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Custom workflow design</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Link2 className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">2. Integrate Systems</h3>
                <p className="text-muted-foreground mb-4">
                  Seamless connection to your existing technology stack including phone systems, CRM, calendars, payment processors, and project management tools. All data flows automatically without manual intervention.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>API and webhook configuration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Real-time data synchronization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Security and compliance setup</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">3. Launch & Optimize</h3>
                <p className="text-muted-foreground mb-4">
                  Your AI employee goes live handling calls, appointments, and customer interactions immediately. Continuous monitoring, performance analytics, and iterative improvements ensure optimal results.
                </p>
                <ul className="text-sm text-muted-foreground space-y-2 text-left">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Immediate 24/7 availability</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Performance tracking dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0"></div>
                    <span>Ongoing optimization and support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
          
          {/* Benefits Summary */}
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6 text-center">Implementation Benefits</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">Zero Risk Trial Period</h4>
                        <p className="text-sm text-muted-foreground">30-day money-back guarantee if you're not satisfied with the results</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">Dedicated Support Team</h4>
                        <p className="text-sm text-muted-foreground">Your success manager ensures smooth operation and continuous improvement</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">Rapid Deployment</h4>
                        <p className="text-sm text-muted-foreground">Go live in 48-72 hours with minimal team involvement required</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <DollarSign className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">Immediate ROI</h4>
                        <p className="text-sm text-muted-foreground">Most clients see positive returns within the first 30 days of operation</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Solutions Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Transform Your Business Operations
            </h2>
            <p className="text-xl text-muted-foreground">
              See the before and after with Virtual Employees
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SolutionCard
              icon={Phone}
              title="24/7 Customer Service"
              before="Customers reach voicemail, competitors get the business"
              after="Every call answered instantly, appointments booked automatically"
            />
            <SolutionCard
              icon={Calendar}
              title="Appointment Scheduling"
              before="Phone tag, double bookings, manual calendar management"
              after="Smart scheduling with automatic confirmations and reminders"
            />
            <SolutionCard
              icon={Filter}
              title="Lead Qualification"
              before="Waste time on unqualified leads"
              after="AI pre-screens and routes high-value prospects to your team"
            />
            <SolutionCard
              icon={FileText}
              title="Administrative Tasks"
              before="Hours spent on data entry and follow-ups"
              after="Automated documentation, CRM updates, and customer follow-ups"
            />
            <SolutionCard
              icon={Globe}
              title="Multilingual Support"
              before="Turn away non-English speaking customers"
              after="Serve customers in their preferred language"
            />
            <SolutionCard
              icon={Zap}
              title="Emergency Dispatch"
              before="Critical calls missed during busy periods"
              after="Intelligent priority routing and instant notifications"
            />
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section id="calculator" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How Much Are Missed Calls Costing You?
            </h2>
            <p className="text-xl text-muted-foreground">
              Calculate your potential savings with a Virtual Employee
            </p>
          </div>
          <ROICalculator />
        </div>
      </section>

      {/* Industry-Specific Solutions */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Tailored Solutions for Your Industry
            </h2>
            <p className="text-xl text-muted-foreground">
              See how Virtual Employees work in your specific field
            </p>
          </div>
          <IndustryTabs />
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 text-center mb-12">
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                500+
              </div>
              <p className="text-muted-foreground">Businesses Served</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                1M+
              </div>
              <p className="text-muted-foreground">Calls Handled</p>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                99.9%
              </div>
              <p className="text-muted-foreground">Uptime Guarantee</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card>
              <CardContent className="p-6">
                <p className="text-lg mb-4 italic">
                  "We went from missing 30% of calls to answering 100%. Our booking
                  rate increased by 45% in the first month."
                </p>
                <p className="font-semibold">- Sarah M., Healthcare Clinic</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <p className="text-lg mb-4 italic">
                  "The Virtual Employee pays for itself 10x over. We're capturing
                  emergency calls we used to miss completely."
                </p>
                <p className="font-semibold">- Mike T., HVAC Company</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <ComparisonTable />
        </div>
      </section>

      {/* Technical Trust Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Enterprise-Grade Technology You Can Trust
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <Shield className="h-12 w-12 text-primary mx-auto" />
                <h3 className="font-semibold">Security</h3>
                <p className="text-sm text-muted-foreground">
                  Enterprise-grade encryption, HIPAA compliant
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <Zap className="h-12 w-12 text-primary mx-auto" />
                <h3 className="font-semibold">Reliability</h3>
                <p className="text-sm text-muted-foreground">
                  99.9% uptime, backed by robust infrastructure
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <Link2 className="h-12 w-12 text-primary mx-auto" />
                <h3 className="font-semibold">Integration</h3>
                <p className="text-sm text-muted-foreground">
                  Works with 50+ popular business tools
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center space-y-3">
                <Sliders className="h-12 w-12 text-primary mx-auto" />
                <h3 className="font-semibold">Customization</h3>
                <p className="text-sm text-muted-foreground">
                  Trained on YOUR business in days, not months
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Strong CTA Section */}
      <section id="consultation" className="py-20 bg-gradient-to-b from-primary/5 to-background">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold">
              Don't Know Where to Start?
            </h2>
            <p className="text-xl text-muted-foreground">
              Schedule a call with one of our AI Specialists to discover how a Virtual Employee can transform your business
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <Card>
                <CardContent className="p-6">
                  <p className="font-semibold mb-2">Free Consultation</p>
                  <p className="text-sm text-muted-foreground">
                    No-pressure conversation about your specific needs
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <p className="font-semibold mb-2">Custom Solutions</p>
                  <p className="text-sm text-muted-foreground">
                    Tailored recommendations for your industry and business
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <p className="font-semibold mb-2">Expert Guidance</p>
                  <p className="text-sm text-muted-foreground">
                    Learn how AI can solve your specific challenges
                  </p>
                </CardContent>
              </Card>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8" asChild>
                <a 
                  href="https://calendar.app.google/HfFwVFvYGx42io1B9" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Schedule a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                <Link to="/contact">Request a Consultation</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Join 500+ businesses already transforming their operations
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
